/**
 * ELITE BATCH 24 PART 7 — E-commerce & Retail Workflows (61-70)
 *
 * PRODUCTION ENHANCEMENTS:
 * 1. Case stamping — node 2 stamps case_id, timestamps, workflow version
 * 2. Confidence-gated AI — aiNodeV2 always outputs confidence_score + evidence
 * 3. Confidence routing — confidenceGate routes auto vs human review
 * 4. Post-execution verification — verifyNode confirms actions succeeded
 * 5. Correct field names: conditionData, approvalGateData, delayAction, code
 *
 * Each workflow: 10 nodes, proper edges, detailed summary.
 */

// ============ BASE HELPERS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

// ============ ENHANCED V2 HELPERS ============

function aiNodeV2(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  const enhancedSystem =
    systemPrompt +
    " Also include in your JSON response: confidence_score (0-100 integer rating your certainty), evidence_summary (one sentence citing the key data points you relied on), and needs_human_review (boolean, true if you are uncertain or the case is unusual).";
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${enhancedSystem}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "choices[0].message.content.confidence_score",
            customName: "ai_confidence",
            type: "number",
          },
          {
            name: "choices[0].message.content.evidence_summary",
            customName: "ai_evidence",
            type: "string",
          },
          {
            name: "choices[0].message.content.needs_human_review",
            customName: "ai_needs_review",
            type: "string",
          },
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function caseStampNode(
  id: string,
  label: string,
  y: number,
  workflowName: string,
  x = 80,
): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Stamp case with unique ID, timestamp, and workflow version
const ts = Date.now();
const rand = Math.random().toString(36).substring(2, 8);
output.case_id = 'case_' + ts + '_' + rand;
output.workflow_name = '${workflowName}';
output.workflow_version = '1.0';
output.started_at = new Date(ts).toISOString();
output.source_channel = variables.source_channel || 'webhook';
output.status = 'intake_complete';`,
    },
    x,
  );
}

function confidenceGate(
  id: string,
  label: string,
  y: number,
  threshold = "75",
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable: "ai_confidence",
          value: threshold,
          comparison_operator: "GREATER THAN",
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function conditionNode(
  id: string,
  label: string,
  y: number,
  variable: string,
  operator: string,
  value: string,
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable,
          value,
          comparison_operator: operator,
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function approvalNode(
  id: string,
  label: string,
  y: number,
  message: string,
  timeoutHours = 24,
  x = 80,
): any {
  return n(
    id,
    "approvalGate",
    label,
    y,
    {
      approvalGateData: {
        approvers: [],
        message,
        channel: "email",
        timeoutHours,
      },
    },
    x,
  );
}

function verifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  url: string,
  headers: Array<{ key: string; value: string }>,
  expectedVars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url,
        headers,
        body: [],
        variables: [
          ...expectedVars,
          { name: "status_code", customName: "verify_status", type: "number" },
        ],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 24 PART 7 — E-commerce & Retail Workflows (61-70)
// =============================================================================

export const ELITE_BATCH24_PART7: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 61. Elite: Order Fraud Reviewer
  // ===================================================================
  "Elite: Order Fraud Reviewer": {
    nodes: [
      trigger("Shopify: High-risk order flagged", "webhook_app", {
        source: "shopify",
        event: "orders/updated",
        howItStarts:
          "Fires when Shopify flags an order as high-risk based on its built-in fraud analysis, including IP mismatch, velocity checks, or AVS/CVV failures.",
        setup:
          "In Shopify Admin > Settings > Notifications, configure the orders/updated webhook. Filter in workflow for risk_level=high. Ensure Shopify Fraud Protect or a third-party fraud app is active.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Fraud review case",
        200,
        "Order Fraud Reviewer",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze fraud signals with evidence",
        360,
        "You are an e-commerce fraud analyst. Evaluate the order for fraud risk by analyzing billing/shipping address mismatch, IP geolocation vs address country, email domain age, order velocity for this customer, AVS/CVV response codes, and historical chargeback patterns. Provide a fraud_verdict (approve/reject/review), risk_factors array listing each signal, and a recommended_action.",
        "Analyze this Shopify high-risk order: Order ID {{{1.id}}}, email {{{1.email}}}, billing_country {{{1.billing_address.country}}}, shipping_country {{{1.shipping_address.country}}}, ip_address {{{1.browser_ip}}}, total {{{1.total_price}}} {{{1.currency}}}, risk_recommendation {{{1.fraud_risk.recommendation}}}. Customer history from enrichment: {{{4.order_count}}} orders, {{{4.total_spent}}} total spent, {{{4.chargeback_count}}} chargebacks.",
        [
          { field: "fraud_verdict", as: "fraud_verdict", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "risk_score", as: "risk_score", type: "number" },
        ],
      ),

      n("4", "hitApi", "Shopify + Stripe: Fetch customer history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/customers/{{{1.customer.id}}}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "customer.orders_count",
              customName: "order_count",
              type: "number",
            },
            {
              name: "customer.total_spent",
              customName: "total_spent",
              type: "string",
            },
            {
              name: "customer.verified_email",
              customName: "email_verified",
              type: "string",
            },
            {
              name: "customer.tags",
              customName: "customer_tags",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI confidence >= 85?", 680, "85"),

      n("6", "hitApi", "Shopify: Auto-release order for fulfillment", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/orders/{{{1.id}}}/risks.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "risk",
              value:
                '{"message":"AI fraud review passed — auto-approved","recommendation":"accept","source":"OmniEngage Fraud Reviewer","cause_cancel":false,"score":0.1}',
            },
          ],
          variables: [
            { name: "risk.id", customName: "risk_entry_id", type: "string" },
          ],
        },
      }),

      approvalNode(
        "7",
        "Approval: Fraud team manual review",
        840,
        "Order #{{{1.order_number}}} flagged for manual fraud review. AI verdict: {{{3.fraud_verdict}}}. Risk factors: {{{3.risk_factors}}}. Confidence: {{{3.ai_confidence}}}%. Customer history: {{{4.order_count}}} orders, ${{{4.total_spent}}} spent. Please approve to release or reject to cancel.",
        4,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Order status confirmed",
        1000,
        "GET",
        "https://{shop_domain}.myshopify.com/admin/api/2024-01/orders/{{{1.id}}}.json",
        [{ key: "X-Shopify-Access-Token", value: "{shopify_token}" }],
        [
          {
            name: "order.financial_status",
            customName: "financial_status",
            type: "string",
          },
          {
            name: "order.confirmed",
            customName: "order_confirmed",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Order confirmed?",
        1160,
        "order_confirmed",
        "EQUALS",
        "true",
      ),

      sheetsNode(
        "10",
        "Sheets: Log fraud review outcome",
        1320,
        "FraudReviews",
        '[["{{{2.case_id}}}","{{{1.order_number}}}","{{{1.email}}}","{{{3.fraud_verdict}}}","{{{3.risk_score}}}","{{{3.ai_confidence}}}","{{{8.financial_status}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Intercepts Shopify high-risk orders, stamps a fraud case, uses AI to analyze fraud signals (address mismatch, IP geolocation, velocity, chargebacks) with evidence and confidence scoring. High-confidence verdicts auto-release orders; low-confidence cases route to a fraud team approval gate. Verifies order status post-action and logs every review to Google Sheets for audit trails.",
  },

  // ===================================================================
  // 62. Elite: Cart Recovery with Personalization
  // ===================================================================
  "Elite: Cart Recovery with Personalization": {
    nodes: [
      trigger("Shopify: Cart abandoned detected", "webhook_app", {
        source: "shopify",
        event: "carts/update",
        howItStarts:
          "Fires when a Shopify cart has been inactive for 60+ minutes, indicating abandonment. The webhook delivers cart contents, customer info, and cart value.",
        setup:
          "Configure Shopify abandoned checkout webhook. Set a delay filter for 60-minute inactivity threshold. Ensure customer email capture is enabled on your storefront.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Cart recovery case",
        200,
        "Cart Recovery with Personalization",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze cart and generate personalized message",
        360,
        "You are an e-commerce conversion specialist. Analyze the abandoned cart contents, customer purchase history, and browsing behavior to generate a highly personalized recovery email. Consider the product categories, price sensitivity signals, and past purchase patterns. Output a personalized_subject, personalized_body (HTML email), recommended_incentive (none/free_shipping/percentage_off/bundle_deal), and incentive_value.",
        "Abandoned cart: items {{{1.line_items}}}, cart_total {{{1.total_price}}}, customer_email {{{1.email}}}. Customer profile from HubSpot: lifetime_value {{{4.ltv}}}, purchase_count {{{4.purchases}}}, last_purchase_days_ago {{{4.days_since_last}}}. Generate a personalized recovery email.",
        [
          {
            field: "personalized_subject",
            as: "personalized_subject",
            type: "string",
          },
          {
            field: "personalized_body",
            as: "personalized_body",
            type: "string",
          },
          {
            field: "recommended_incentive",
            as: "recommended_incentive",
            type: "string",
          },
          { field: "incentive_value", as: "incentive_value", type: "string" },
        ],
      ),

      n("4", "hitApi", "HubSpot: Fetch customer purchase history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{{{1.email}}}?idProperty=email&properties=lifecyclestage,hs_object_id,num_purchases,total_revenue,days_since_last_purchase",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.total_revenue",
              customName: "ltv",
              type: "string",
            },
            {
              name: "properties.num_purchases",
              customName: "purchases",
              type: "string",
            },
            {
              name: "properties.days_since_last_purchase",
              customName: "days_since_last",
              type: "string",
            },
            {
              name: "properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI personalization confidence?", 680),

      emailNode(
        "6",
        "Email: Send personalized recovery",
        840,
        "{{{1.email}}}",
        "{{{3.personalized_subject}}}",
        "{{{3.personalized_body}}}",
      ),

      approvalNode(
        "7",
        "Approval: Marketing reviews recovery email",
        840,
        "Cart recovery email for {{{1.email}}} (cart value: ${{{1.total_price}}}). AI recommends incentive: {{{3.recommended_incentive}}} ({{{3.incentive_value}}}). Confidence: {{{3.ai_confidence}}}%. Subject: {{{3.personalized_subject}}}. Approve to send or reject to skip.",
        12,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Email delivery confirmed",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={{{1.email}}}&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          { name: "TotalCount", customName: "emails_found", type: "number" },
          {
            name: "Messages[0].Status",
            customName: "email_status",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Email delivered?",
        1160,
        "email_status",
        "EQUALS",
        "Sent",
      ),

      sheetsNode(
        "10",
        "Sheets: Log cart recovery attempt",
        1320,
        "CartRecovery",
        '[["{{{2.case_id}}}","{{{1.email}}}","{{{1.total_price}}}","{{{3.recommended_incentive}}}","{{{3.incentive_value}}}","{{{3.ai_confidence}}}","{{{8.email_status}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Captures abandoned Shopify carts, stamps a recovery case, enriches with HubSpot customer history, then uses AI to craft a personalized recovery email with smart incentive recommendations based on LTV and purchase patterns. High-confidence emails send automatically; lower-confidence ones route to marketing approval. Verifies delivery via Postmark and logs all recovery attempts to Sheets.",
  },

  // ===================================================================
  // 63. Elite: Return Inspection Processor
  // ===================================================================
  "Elite: Return Inspection Processor": {
    nodes: [
      trigger("Warehouse: Return item received", "webhook_app", {
        source: "returnly",
        event: "return.received",
        howItStarts:
          "Fires when the warehouse scans a returned item and marks it as received in the returns management system, including item condition notes and photos.",
        setup:
          "Connect your returns platform (Returnly, Loop, or custom) to send a webhook when items are scanned at receiving. Include order_id, item_sku, condition_notes, and photo_urls in the payload.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Return inspection case",
        200,
        "Return Inspection Processor",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Evaluate inspection and determine refund",
        360,
        "You are a returns quality inspector. Evaluate the returned item based on the condition notes, product category, original order details, and return reason. Determine the refund_type (full/partial/store_credit/deny), refund_percentage (0-100), restocking_decision (restock/refurbish/dispose), and quality_grade (A/B/C/D). Consider the return policy window and item condition.",
        "Return received: order_id {{{1.order_id}}}, sku {{{1.item_sku}}}, reason {{{1.return_reason}}}, condition_notes {{{1.condition_notes}}}. Original order from Shopify: order_total {{{4.order_total}}}, item_price {{{4.item_price}}}, order_date {{{4.order_date}}}, days_since_purchase {{{4.days_since_purchase}}}. Determine refund eligibility and amount.",
        [
          { field: "refund_type", as: "refund_type", type: "string" },
          {
            field: "refund_percentage",
            as: "refund_percentage",
            type: "number",
          },
          {
            field: "restocking_decision",
            as: "restocking_decision",
            type: "string",
          },
          { field: "quality_grade", as: "quality_grade", type: "string" },
          { field: "refund_amount", as: "refund_amount", type: "string" },
        ],
      ),

      n("4", "hitApi", "Shopify: Fetch original order details", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/orders/{{{1.order_id}}}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "order.total_price",
              customName: "order_total",
              type: "string",
            },
            {
              name: "order.line_items[0].price",
              customName: "item_price",
              type: "string",
            },
            {
              name: "order.created_at",
              customName: "order_date",
              type: "string",
            },
            {
              name: "order.customer.email",
              customName: "customer_email",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI inspection confidence?", 680),

      n("6", "hitApi", "Shopify: Process refund", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/orders/{{{1.order_id}}}/refunds.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "refund",
              value:
                '{"note":"Auto-processed by OmniEngage Return Inspector. Grade: {{{3.quality_grade}}}. Type: {{{3.refund_type}}}","shipping":{"full_refund":false},"transactions":[{"kind":"refund","amount":"{{{3.refund_amount}}}"}]}',
            },
          ],
          variables: [
            { name: "refund.id", customName: "refund_id", type: "string" },
          ],
        },
      }),

      approvalNode(
        "7",
        "Approval: Support manager reviews refund",
        840,
        "Return for order #{{{1.order_id}}}, SKU {{{1.item_sku}}}. AI assessment: {{{3.refund_type}}} refund ({{{3.refund_percentage}}}%), amount ${{{3.refund_amount}}}. Quality grade: {{{3.quality_grade}}}. Restocking: {{{3.restocking_decision}}}. Confidence: {{{3.ai_confidence}}}%. Approve to process refund.",
        8,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Refund issued in Shopify",
        1000,
        "GET",
        "https://{shop_domain}.myshopify.com/admin/api/2024-01/orders/{{{1.order_id}}}/refunds.json",
        [{ key: "X-Shopify-Access-Token", value: "{shopify_token}" }],
        [
          {
            name: "refunds[0].id",
            customName: "verified_refund_id",
            type: "string",
          },
          {
            name: "refunds[0].processed_at",
            customName: "refund_processed_at",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Refund processed?",
        1160,
        "verified_refund_id",
        "IS NOT EMPTY",
        "",
      ),

      sheetsNode(
        "10",
        "Sheets: Log return inspection",
        1320,
        "Returns",
        '[["{{{2.case_id}}}","{{{1.order_id}}}","{{{1.item_sku}}}","{{{3.quality_grade}}}","{{{3.refund_type}}}","{{{3.refund_amount}}}","{{{3.restocking_decision}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Processes returned items by stamping a case, fetching original order data from Shopify, then using AI to evaluate item condition, determine refund type/amount, and assign quality grades. High-confidence refunds process automatically; edge cases route to support manager approval. Verifies refund completion in Shopify and logs every inspection to Sheets for inventory and finance reconciliation.",
  },

  // ===================================================================
  // 64. Elite: Subscription Churn Preventer
  // ===================================================================
  "Elite: Subscription Churn Preventer": {
    nodes: [
      trigger("Stripe: Subscription cancellation initiated", "webhook_app", {
        source: "stripe",
        event: "customer.subscription.updated",
        howItStarts:
          "Fires when a customer initiates subscription cancellation in Stripe (cancel_at_period_end set to true), giving you a window to present a retention offer before the subscription actually ends.",
        setup:
          "In Stripe Dashboard > Developers > Webhooks, add the customer.subscription.updated event. Filter for cancel_at_period_end=true to only trigger on cancellation attempts.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Churn prevention case",
        200,
        "Subscription Churn Preventer",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Analyze churn reason and recommend save offer",
        360,
        "You are a subscription retention specialist. Analyze the customer's cancellation reason, usage patterns, subscription tier, lifetime value, and support history to recommend the best retention offer. Output churn_risk_category (price/feature/competitor/support/other), recommended_offer (discount/pause/downgrade/feature_unlock/none), offer_details (specific terms), win_back_message (personalized retention pitch), and estimated_save_probability (0-100).",
        "Subscription cancellation: customer_id {{{1.customer}}}, plan {{{1.plan.id}}}, mrr {{{1.plan.amount}}}, cancel_reason {{{1.cancellation_details.reason}}}. Customer enrichment: HubSpot LTV {{{4.ltv}}}, months_active {{{4.months_active}}}, support_tickets {{{4.tickets}}}, NPS_score {{{4.nps}}}. Recommend the best retention strategy.",
        [
          {
            field: "churn_risk_category",
            as: "churn_risk_category",
            type: "string",
          },
          {
            field: "recommended_offer",
            as: "recommended_offer",
            type: "string",
          },
          { field: "offer_details", as: "offer_details", type: "string" },
          { field: "win_back_message", as: "win_back_message", type: "string" },
          {
            field: "estimated_save_probability",
            as: "save_probability",
            type: "number",
          },
        ],
      ),

      n("4", "hitApi", "Stripe + HubSpot: Fetch customer context", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{{{1.customer_email}}}?idProperty=email&properties=hs_object_id,total_revenue,num_associated_deals,hs_ticket_count,nps_score,createdate",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.total_revenue",
              customName: "ltv",
              type: "string",
            },
            {
              name: "properties.createdate",
              customName: "months_active",
              type: "string",
            },
            {
              name: "properties.hs_ticket_count",
              customName: "tickets",
              type: "string",
            },
            { name: "properties.nps_score", customName: "nps", type: "string" },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI retention confidence?", 680),

      emailNode(
        "6",
        "Email: Present retention offer",
        840,
        "{{{1.customer_email}}}",
        "We'd hate to see you go — here's something special",
        "{{{3.win_back_message}}}",
      ),

      approvalNode(
        "7",
        "Approval: CSM reviews retention offer",
        840,
        "Customer {{{1.customer_email}}} cancelling {{{1.plan.id}}} (${{{1.plan.amount}}}/mo). Churn reason: {{{3.churn_risk_category}}}. AI recommends: {{{3.recommended_offer}}} — {{{3.offer_details}}}. Save probability: {{{3.save_probability}}}%. Confidence: {{{3.ai_confidence}}}%. Approve to send retention offer.",
        12,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Offer email delivered",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={{{1.customer_email}}}&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          { name: "TotalCount", customName: "emails_found", type: "number" },
          {
            name: "Messages[0].Status",
            customName: "email_status",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Offer presented?",
        1160,
        "email_status",
        "EQUALS",
        "Sent",
      ),

      sheetsNode(
        "10",
        "Sheets: Log churn prevention attempt",
        1320,
        "ChurnPrevention",
        '[["{{{2.case_id}}}","{{{1.customer_email}}}","{{{1.plan.id}}}","{{{3.churn_risk_category}}}","{{{3.recommended_offer}}}","{{{3.offer_details}}}","{{{3.save_probability}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Intercepts Stripe subscription cancellations, enriches with HubSpot customer context (LTV, tenure, NPS, tickets), then uses AI to categorize churn reason and recommend the optimal save offer. High-confidence offers send automatically via personalized email; uncertain cases route to CSM approval. Verifies delivery and logs every retention attempt for churn analytics.",
  },

  // ===================================================================
  // 65. Elite: Inventory Reorder Trigger
  // ===================================================================
  "Elite: Inventory Reorder Trigger": {
    nodes: [
      trigger("Schedule: Daily inventory check", "schedule", {
        schedule: "Daily at 7:00 AM — scans all SKUs for reorder thresholds",
        howItStarts:
          "Runs daily to check Shopify inventory levels against reorder points. Identifies SKUs that need replenishment based on current stock, sales velocity, and lead times.",
        setup:
          "Configure schedule for daily 7 AM execution. Set reorder thresholds per product category in your inventory management settings. Ensure Shopify inventory tracking is enabled for all products.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Inventory reorder case",
        200,
        "Inventory Reorder Trigger",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Predict reorder needs and quantities",
        360,
        "You are an inventory planning specialist. Analyze current stock levels, 30-day sales velocity, seasonal trends, and supplier lead times to determine which SKUs need reordering. For each SKU below reorder point, calculate optimal_order_quantity using economic order quantity principles. Output reorder_skus (comma-separated list), total_skus_to_reorder (count), highest_priority_sku, estimated_stockout_days (for most urgent), total_po_value, and supplier_name.",
        "Shopify inventory snapshot: {{{4.inventory_data}}}. Analyze these stock levels against sales velocity. Products below 20% of 30-day average need reordering. Calculate order quantities with a 2-week lead time buffer.",
        [
          { field: "reorder_skus", as: "reorder_skus", type: "string" },
          {
            field: "total_skus_to_reorder",
            as: "total_skus_to_reorder",
            type: "number",
          },
          {
            field: "highest_priority_sku",
            as: "highest_priority_sku",
            type: "string",
          },
          {
            field: "estimated_stockout_days",
            as: "estimated_stockout_days",
            type: "number",
          },
          { field: "total_po_value", as: "total_po_value", type: "string" },
          { field: "supplier_name", as: "supplier_name", type: "string" },
        ],
      ),

      n("4", "hitApi", "Shopify: Fetch current inventory levels", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/inventory_levels.json?location_ids={location_id}&limit=250",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "inventory_levels",
              customName: "inventory_data",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI reorder confidence?", 680),

      emailNode(
        "6",
        "Email: Send PO to supplier",
        840,
        "{{{3.supplier_name}}}@supplier.com",
        "Purchase Order — {{{3.total_skus_to_reorder}}} SKUs from OmniEngage Store",
        "Purchase Order Request\n\nSKUs to reorder: {{{3.reorder_skus}}}\nTotal items: {{{3.total_skus_to_reorder}}}\nHighest priority: {{{3.highest_priority_sku}}} (est. stockout in {{{3.estimated_stockout_days}}} days)\nEstimated PO value: ${{{3.total_po_value}}}\n\nPlease confirm availability and lead times.\n\nCase ID: {{{2.case_id}}}",
      ),

      approvalNode(
        "7",
        "Approval: Procurement reviews PO",
        840,
        "AI recommends reordering {{{3.total_skus_to_reorder}}} SKUs. Highest priority: {{{3.highest_priority_sku}}} (stockout in {{{3.estimated_stockout_days}}} days). Total PO value: ${{{3.total_po_value}}}. Confidence: {{{3.ai_confidence}}}%. Approve to send PO to supplier.",
        24,
        480,
      ),

      verifyNode(
        "8",
        "Verify: PO email sent",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?tag=purchase-order&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          { name: "TotalCount", customName: "po_emails_sent", type: "number" },
          {
            name: "Messages[0].Status",
            customName: "po_email_status",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: PO sent successfully?",
        1160,
        "po_email_status",
        "EQUALS",
        "Sent",
      ),

      sheetsNode(
        "10",
        "Sheets: Log inventory reorder",
        1320,
        "InventoryReorders",
        '[["{{{2.case_id}}}","{{{3.total_skus_to_reorder}}}","{{{3.highest_priority_sku}}}","{{{3.estimated_stockout_days}}}","{{{3.total_po_value}}}","{{{3.supplier_name}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Runs daily to scan Shopify inventory levels, uses AI to predict reorder needs based on sales velocity and lead times, then generates and sends purchase orders to suppliers. High-confidence reorders send automatically; large or uncertain POs route to procurement approval. Verifies PO delivery and logs all reorder events for inventory management tracking.",
  },

  // ===================================================================
  // 66. Elite: Product Review Response
  // ===================================================================
  "Elite: Product Review Response": {
    nodes: [
      trigger("Shopify: New product review submitted", "webhook_app", {
        source: "shopify",
        event: "product_review.created",
        howItStarts:
          "Fires when a customer submits a product review on your Shopify store via a reviews app (Judge.me, Stamped.io, or Shopify Reviews). Includes rating, review text, product details, and reviewer info.",
        setup:
          "Connect your Shopify reviews app webhook to fire on new review submissions. Ensure the payload includes rating, body, product_id, and reviewer email.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Review response case",
        200,
        "Product Review Response",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Classify sentiment and draft response",
        360,
        "You are a brand voice specialist for an e-commerce store. Analyze the product review to classify sentiment, identify specific praise or complaints, and draft an appropriate public response. For positive reviews, express genuine gratitude and highlight the specific features they enjoyed. For negative reviews, show empathy, address specific concerns, and offer concrete resolution. Output sentiment (positive/neutral/negative), key_topics (comma-separated), draft_response (the public reply text), internal_action (none/follow_up/escalate/refund), and urgency (low/medium/high).",
        "New review: product {{{1.product_title}}}, rating {{{1.rating}}}/5, review text: {{{1.body}}}. Reviewer: {{{1.reviewer_name}}}. HubSpot match: customer since {{{4.customer_since}}}, order_count {{{4.order_count}}}, VIP status {{{4.vip_status}}}. Draft a brand-appropriate response.",
        [
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "key_topics", as: "key_topics", type: "string" },
          { field: "draft_response", as: "draft_response", type: "string" },
          { field: "internal_action", as: "internal_action", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      n("4", "hitApi", "HubSpot: Match reviewer to customer profile", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{{{1.reviewer_email}}}?idProperty=email&properties=createdate,num_purchases,hs_lead_status",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.createdate",
              customName: "customer_since",
              type: "string",
            },
            {
              name: "properties.num_purchases",
              customName: "order_count",
              type: "string",
            },
            {
              name: "properties.hs_lead_status",
              customName: "vip_status",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI response confidence?", 680),

      n("6", "hitApi", "Reviews App: Post public response", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://judge.me/api/v1/reviews/{{{1.review_id}}}/replies",
          headers: [
            { key: "Authorization", value: "Bearer {judgeme_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "body", value: "{{{3.draft_response}}}" }],
          variables: [{ name: "id", customName: "reply_id", type: "string" }],
        },
      }),

      approvalNode(
        "7",
        "Approval: Brand manager reviews response",
        840,
        'New {{{3.sentiment}}} review ({{{1.rating}}}/5) on {{{1.product_title}}} by {{{1.reviewer_name}}}. Topics: {{{3.key_topics}}}. AI drafted response: "{{{3.draft_response}}}". Internal action: {{{3.internal_action}}}. Confidence: {{{3.ai_confidence}}}%. Approve to post publicly.',
        12,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Response posted",
        1000,
        "GET",
        "https://judge.me/api/v1/reviews/{{{1.review_id}}}",
        [{ key: "Authorization", value: "Bearer {judgeme_token}" }],
        [
          {
            name: "review.replies_count",
            customName: "replies_count",
            type: "number",
          },
          { name: "review.replied", customName: "has_reply", type: "string" },
        ],
      ),

      conditionNode(
        "9",
        "Check: Response posted?",
        1160,
        "has_reply",
        "EQUALS",
        "true",
      ),

      sheetsNode(
        "10",
        "Sheets: Log review response",
        1320,
        "ReviewResponses",
        '[["{{{2.case_id}}}","{{{1.product_title}}}","{{{1.rating}}}","{{{3.sentiment}}}","{{{3.key_topics}}}","{{{3.internal_action}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Captures new product reviews, matches reviewers to HubSpot profiles, then uses AI to classify sentiment, extract topics, and draft brand-appropriate public responses. Positive and routine responses auto-post; sensitive or negative reviews route to brand manager approval. Verifies response posting and logs all review interactions for brand reputation analytics.",
  },

  // ===================================================================
  // 67. Elite: Delivery Exception Handler
  // ===================================================================
  "Elite: Delivery Exception Handler": {
    nodes: [
      trigger("ShipStation: Delivery exception detected", "webhook_app", {
        source: "shipstation",
        event: "SHIP_NOTIFY",
        howItStarts:
          "Fires when ShipStation reports a delivery exception — failed delivery attempt, address undeliverable, package damaged in transit, or customs hold. Includes tracking number, exception type, and order details.",
        setup:
          "In ShipStation > Settings > Integrations, configure webhook for shipping exceptions. Ensure tracking events include exception codes (FTD, UTL, DMG, CUS). Map ShipStation order IDs to Shopify order numbers.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Delivery exception case",
        200,
        "Delivery Exception Handler",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Classify failure and recommend rescue action",
        360,
        "You are a shipping operations specialist. Classify the delivery exception and recommend the best rescue action. Consider the exception type, order value, customer history, and shipping destination. Output exception_category (address_issue/carrier_damage/customs_hold/failed_attempt/lost), rescue_action (reship/refund/address_correction/customs_docs/retry_delivery), customer_message (empathetic proactive update), priority (urgent/standard), and estimated_resolution_days.",
        "Delivery exception: tracking {{{1.tracking_number}}}, exception {{{1.exception_description}}}, carrier {{{1.carrier_code}}}. Shopify order: order_number {{{4.order_number}}}, customer_email {{{4.customer_email}}}, order_value {{{4.order_total}}}, shipping_address {{{4.shipping_address}}}. Classify and recommend rescue.",
        [
          {
            field: "exception_category",
            as: "exception_category",
            type: "string",
          },
          { field: "rescue_action", as: "rescue_action", type: "string" },
          { field: "customer_message", as: "customer_message", type: "string" },
          { field: "priority", as: "priority", type: "string" },
          {
            field: "estimated_resolution_days",
            as: "resolution_days",
            type: "number",
          },
        ],
      ),

      n("4", "hitApi", "ShipStation + Shopify: Fetch order details", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://ssapi.shipstation.com/orders?orderNumber={{{1.order_key}}}",
          headers: [
            { key: "Authorization", value: "Basic {shipstation_auth}" },
          ],
          body: [],
          variables: [
            {
              name: "orders[0].orderNumber",
              customName: "order_number",
              type: "string",
            },
            {
              name: "orders[0].shipTo.name",
              customName: "customer_name",
              type: "string",
            },
            {
              name: "orders[0].customerEmail",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "orders[0].orderTotal",
              customName: "order_total",
              type: "string",
            },
            {
              name: "orders[0].shipTo.street1",
              customName: "shipping_address",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI classification confidence?", 680),

      emailNode(
        "6",
        "Email: Proactive customer update",
        840,
        "{{{4.customer_email}}}",
        "Update on your order #{{{4.order_number}}} — we're on it",
        "{{{3.customer_message}}}",
      ),

      approvalNode(
        "7",
        "Approval: Support reviews exception handling",
        840,
        "Delivery exception for order #{{{4.order_number}}} ({{{4.customer_name}}}). Exception: {{{3.exception_category}}}. AI recommends: {{{3.rescue_action}}}. Order value: ${{{4.order_total}}}. Est. resolution: {{{3.resolution_days}}} days. Confidence: {{{3.ai_confidence}}}%. Approve to send proactive customer update.",
        8,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Customer notified",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={{{4.customer_email}}}&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          { name: "TotalCount", customName: "emails_found", type: "number" },
          {
            name: "Messages[0].Status",
            customName: "notify_status",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Customer notified?",
        1160,
        "notify_status",
        "EQUALS",
        "Sent",
      ),

      sheetsNode(
        "10",
        "Sheets: Log delivery exception",
        1320,
        "DeliveryExceptions",
        '[["{{{2.case_id}}}","{{{4.order_number}}}","{{{1.tracking_number}}}","{{{3.exception_category}}}","{{{3.rescue_action}}}","{{{3.priority}}}","{{{3.resolution_days}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Intercepts ShipStation delivery exceptions, fetches order details, then uses AI to classify the failure type and recommend rescue actions (reship, refund, address correction). High-confidence cases auto-send proactive customer updates; complex exceptions route to support approval. Verifies notification delivery and logs all exceptions for shipping performance analytics.",
  },

  // ===================================================================
  // 68. Elite: VIP Order Concierge
  // ===================================================================
  "Elite: VIP Order Concierge": {
    nodes: [
      trigger("Shopify: VIP customer order placed", "webhook_app", {
        source: "shopify",
        event: "orders/create",
        howItStarts:
          "Fires when an order is placed by a customer tagged as VIP in Shopify (based on loyalty tier, lifetime spend, or manual tagging). These orders receive white-glove treatment with priority shipping and personalized communications.",
        setup:
          "Configure Shopify orders/create webhook. Add a filter for customer tags containing 'VIP', 'platinum', or 'gold'. Ensure HubSpot has loyalty program data synced with customer records.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: VIP concierge case",
        200,
        "VIP Order Concierge",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Verify VIP status and generate personal note",
        360,
        "You are a luxury customer experience concierge. Verify the customer's VIP status using their loyalty data and order history, then craft a warm, personalized thank-you note that references their specific purchase, loyalty milestone, and relationship with the brand. Output vip_tier_verified (gold/platinum/diamond), personal_note (the handwritten-style message), gift_recommendation (small surprise add-on if applicable), shipping_priority (express/overnight/same_day), and loyalty_points_earned.",
        "VIP order: order_number {{{1.order_number}}}, items {{{1.line_items}}}, total {{{1.total_price}}}. Customer: {{{1.customer.first_name}}} {{{1.customer.last_name}}}, email {{{1.customer.email}}}. HubSpot loyalty data: tier {{{4.loyalty_tier}}}, lifetime_value {{{4.ltv}}}, member_since {{{4.member_since}}}, favorite_category {{{4.favorite_category}}}. Craft a personalized VIP experience.",
        [
          { field: "vip_tier_verified", as: "vip_tier", type: "string" },
          { field: "personal_note", as: "personal_note", type: "string" },
          {
            field: "gift_recommendation",
            as: "gift_recommendation",
            type: "string",
          },
          {
            field: "shipping_priority",
            as: "shipping_priority",
            type: "string",
          },
          {
            field: "loyalty_points_earned",
            as: "points_earned",
            type: "number",
          },
        ],
      ),

      n("4", "hitApi", "HubSpot: Fetch loyalty and preference data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{{{1.customer.email}}}?idProperty=email&properties=loyalty_tier,total_revenue,createdate,favorite_product_category",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.loyalty_tier",
              customName: "loyalty_tier",
              type: "string",
            },
            {
              name: "properties.total_revenue",
              customName: "ltv",
              type: "string",
            },
            {
              name: "properties.createdate",
              customName: "member_since",
              type: "string",
            },
            {
              name: "properties.favorite_product_category",
              customName: "favorite_category",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI concierge confidence?", 680),

      emailNode(
        "6",
        "Email: Priority ship confirmation + personal note",
        840,
        "{{{1.customer.email}}}",
        "Your order is getting the VIP treatment, {{{1.customer.first_name}}}!",
        "{{{3.personal_note}}}",
      ),

      approvalNode(
        "7",
        "Approval: VIP team reviews experience",
        840,
        "VIP order #{{{1.order_number}}} by {{{1.customer.first_name}}} {{{1.customer.last_name}}} (${{{1.total_price}}}). Tier: {{{3.vip_tier}}}. Shipping: {{{3.shipping_priority}}}. Gift rec: {{{3.gift_recommendation}}}. Points earned: {{{3.points_earned}}}. Confidence: {{{3.ai_confidence}}}%. Approve to send personalized VIP confirmation.",
        4,
        480,
      ),

      verifyNode(
        "8",
        "Verify: VIP order shipped",
        1000,
        "GET",
        "https://{shop_domain}.myshopify.com/admin/api/2024-01/orders/{{{1.id}}}/fulfillments.json",
        [{ key: "X-Shopify-Access-Token", value: "{shopify_token}" }],
        [
          {
            name: "fulfillments[0].status",
            customName: "fulfillment_status",
            type: "string",
          },
          {
            name: "fulfillments[0].tracking_number",
            customName: "tracking_number",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Order shipped?",
        1160,
        "fulfillment_status",
        "EQUALS",
        "success",
      ),

      sheetsNode(
        "10",
        "Sheets: Log VIP concierge",
        1320,
        "VIPConcierge",
        '[["{{{2.case_id}}}","{{{1.order_number}}}","{{{1.customer.email}}}","{{{3.vip_tier}}}","{{{3.shipping_priority}}}","{{{3.gift_recommendation}}}","{{{3.points_earned}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Provides white-glove treatment for VIP orders by verifying loyalty status via HubSpot, using AI to craft personalized thank-you notes, recommend surprise gifts, and assign priority shipping. High-confidence experiences auto-send; edge cases route to VIP team approval. Verifies fulfillment status and logs every concierge interaction for VIP program analytics.",
  },

  // ===================================================================
  // 69. Elite: Price Drop Notifier
  // ===================================================================
  "Elite: Price Drop Notifier": {
    nodes: [
      trigger("Shopify: Product price changed", "webhook_app", {
        source: "shopify",
        event: "products/update",
        howItStarts:
          "Fires when a product price is reduced in Shopify. Compares the new price against the previous price to detect genuine discounts, then identifies customers who previously browsed or wishlisted that product.",
        setup:
          "Configure Shopify products/update webhook. Add price change detection logic (compare_at_price vs price). Ensure HubSpot tracks product page views and wishlist additions via your storefront tracking pixel.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Price drop notification case",
        200,
        "Price Drop Notifier",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Find interested customers and craft alerts",
        360,
        "You are an e-commerce marketing specialist. Analyze the price drop details and customer browse/wishlist data to identify the most interested customers and craft compelling price drop notifications. Segment customers by interest level. Output interested_customer_count, top_segment (wishlist/browsed/cart_abandoned/category_buyer), alert_subject (email subject line), alert_body (email body with urgency and savings), discount_percentage, and savings_amount.",
        "Price drop: product {{{1.title}}}, old_price {{{1.variants[0].compare_at_price}}}, new_price {{{1.variants[0].price}}}, product_id {{{1.id}}}. Browse history from HubSpot: {{{4.interested_contacts}}} contacts viewed this product, {{{4.wishlist_count}}} wishlisted, {{{4.cart_abandon_count}}} abandoned carts with this item. Craft targeted price drop alerts.",
        [
          {
            field: "interested_customer_count",
            as: "interested_count",
            type: "number",
          },
          { field: "top_segment", as: "top_segment", type: "string" },
          { field: "alert_subject", as: "alert_subject", type: "string" },
          { field: "alert_body", as: "alert_body", type: "string" },
          { field: "discount_percentage", as: "discount_pct", type: "string" },
          { field: "savings_amount", as: "savings_amount", type: "string" },
        ],
      ),

      n("4", "hitApi", "Shopify + HubSpot: Fetch product interest data", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"recent_viewed_products","operator":"CONTAINS_TOKEN","value":"{{{1.id}}}"}]}]',
            },
            { key: "limit", value: "100" },
          ],
          variables: [
            {
              name: "total",
              customName: "interested_contacts",
              type: "number",
            },
            {
              name: "results[0].properties.email",
              customName: "first_contact_email",
              type: "string",
            },
            {
              name: "results.length",
              customName: "wishlist_count",
              type: "number",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI alert confidence?", 680),

      emailNode(
        "6",
        "Email: Send price drop alert",
        840,
        "{{{4.first_contact_email}}}",
        "{{{3.alert_subject}}}",
        "{{{3.alert_body}}}",
      ),

      approvalNode(
        "7",
        "Approval: Marketing reviews price alert",
        840,
        "Price drop on {{{1.title}}}: ${{{1.variants[0].compare_at_price}}} -> ${{{1.variants[0].price}}} ({{{3.discount_pct}}}% off). {{{3.interested_count}}} interested customers. Top segment: {{{3.top_segment}}}. Confidence: {{{3.ai_confidence}}}%. Approve to send price drop alerts.",
        8,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Alerts sent",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?tag=price-drop&count=1",
        [{ key: "X-Postmark-Server-Token", value: "{postmark_token}" }],
        [
          { name: "TotalCount", customName: "alerts_sent", type: "number" },
          {
            name: "Messages[0].Status",
            customName: "alert_status",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Alerts sent?",
        1160,
        "alert_status",
        "EQUALS",
        "Sent",
      ),

      sheetsNode(
        "10",
        "Sheets: Log price drop notifications",
        1320,
        "PriceDropAlerts",
        '[["{{{2.case_id}}}","{{{1.title}}}","{{{3.discount_pct}}}","{{{3.savings_amount}}}","{{{3.interested_count}}}","{{{3.top_segment}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Detects Shopify price drops, queries HubSpot for customers who browsed or wishlisted the product, then uses AI to craft targeted price drop notifications with personalized savings messaging. High-confidence alerts auto-send; campaigns with uncertain targeting route to marketing approval. Verifies email delivery and logs all notifications for conversion tracking.",
  },

  // ===================================================================
  // 70. Elite: Gift Card Issuer
  // ===================================================================
  "Elite: Gift Card Issuer": {
    nodes: [
      trigger("Shopify: Gift card request received", "webhook_app", {
        source: "shopify",
        event: "orders/create",
        howItStarts:
          "Fires when a customer purchases a gift card product through Shopify. The workflow validates the request, determines the appropriate card value, creates the gift card, and delivers it to the recipient via email.",
        setup:
          "Configure Shopify orders/create webhook with a filter for orders containing gift card products (product_type=gift_card). Ensure your Shopify plan supports gift card creation via API.",
        inputFields: [],
      }),

      caseStampNode(
        "2",
        "Stamp: Gift card issuance case",
        200,
        "Gift Card Issuer",
      ),

      aiNodeV2(
        "3",
        "OpenAI: Validate request and determine card details",
        360,
        "You are a gift card operations specialist. Validate the gift card purchase request, verify the amount is within policy limits, check for potential fraud signals (bulk purchases, velocity, mismatched billing), and generate a personalized gift card message. Output validated (true/false), card_amount (validated dollar amount), recipient_email, recipient_name, personal_message (for the gift card email), fraud_risk (low/medium/high), and issue_reason if validation fails.",
        "Gift card order: order_id {{{1.id}}}, buyer_email {{{1.email}}}, items {{{1.line_items}}}, total {{{1.total_price}}}. Gift card note: {{{1.note}}}. Shopify order context: billing_name {{{4.billing_name}}}, order_count {{{4.order_count}}}, gift_cards_purchased_30d {{{4.recent_gc_count}}}. Validate and prepare gift card issuance.",
        [
          { field: "validated", as: "gc_validated", type: "string" },
          { field: "card_amount", as: "card_amount", type: "string" },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "personal_message", as: "personal_message", type: "string" },
          { field: "fraud_risk", as: "fraud_risk", type: "string" },
        ],
      ),

      n("4", "hitApi", "Shopify: Fetch order and buyer context", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/orders/{{{1.id}}}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "order.billing_address.name",
              customName: "billing_name",
              type: "string",
            },
            {
              name: "order.customer.orders_count",
              customName: "order_count",
              type: "number",
            },
            { name: "order.note", customName: "gift_note", type: "string" },
            {
              name: "order.customer.tags",
              customName: "customer_tags",
              type: "string",
            },
          ],
        },
      }),

      confidenceGate("5", "Gate: AI validation confidence?", 680),

      n("6", "hitApi", "Shopify: Create gift card and email delivery", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shop_domain}.myshopify.com/admin/api/2024-01/gift_cards.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "gift_card",
              value:
                '{"initial_value":"{{{3.card_amount}}}","note":"Order #{{{1.order_number}}} — {{{3.personal_message}}}","template_suffix":"custom","recipient_email":"{{{3.recipient_email}}}"}',
            },
          ],
          variables: [
            {
              name: "gift_card.id",
              customName: "gift_card_id",
              type: "string",
            },
            {
              name: "gift_card.code",
              customName: "gift_card_code",
              type: "string",
            },
          ],
        },
      }),

      approvalNode(
        "7",
        "Approval: Finance reviews gift card issuance",
        840,
        'Gift card request: ${{{3.card_amount}}} for {{{3.recipient_name}}} ({{{3.recipient_email}}}). Buyer: {{{1.email}}}. Fraud risk: {{{3.fraud_risk}}}. Personal message: "{{{3.personal_message}}}". Confidence: {{{3.ai_confidence}}}%. Approve to create and deliver gift card.',
        12,
        480,
      ),

      verifyNode(
        "8",
        "Verify: Gift card created in Shopify",
        1000,
        "GET",
        "https://{shop_domain}.myshopify.com/admin/api/2024-01/gift_cards/{{{6.gift_card_id}}}.json",
        [{ key: "X-Shopify-Access-Token", value: "{shopify_token}" }],
        [
          {
            name: "gift_card.id",
            customName: "verified_gc_id",
            type: "string",
          },
          {
            name: "gift_card.balance",
            customName: "gc_balance",
            type: "string",
          },
          {
            name: "gift_card.enabled",
            customName: "gc_enabled",
            type: "string",
          },
        ],
      ),

      conditionNode(
        "9",
        "Check: Gift card created?",
        1160,
        "gc_enabled",
        "EQUALS",
        "true",
      ),

      sheetsNode(
        "10",
        "Sheets: Log gift card issuance",
        1320,
        "GiftCards",
        '[["{{{2.case_id}}}","{{{1.order_number}}}","{{{1.email}}}","{{{3.recipient_email}}}","{{{3.card_amount}}}","{{{6.gift_card_code}}}","{{{3.fraud_risk}}}","{{{3.ai_confidence}}}","{{{2.started_at}}}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Processes Shopify gift card purchases by validating the request, checking for fraud signals, and generating personalized gift card messages via AI. High-confidence issuances auto-create the card in Shopify and email it to the recipient; flagged requests route to finance approval. Verifies card creation and balance, then logs every issuance for financial tracking and fraud monitoring.",
  },
};
